<template>
	<drawer
			title="Pay invoice"
			z-index="10"
			@updateClose="onClose"
			@updateSave="onSave"
	>
		<div
				v-if="incomeTransaction"
				class="paidTransactions"
		>
			<div class="subtext">
				Transaction ID: {{ incomeTransaction.id }}
			</div>

			<!-- Customer -->
			<field
					margin="20px 0 10px 0"
					width="300px"
					label="Customer"
					:setInputValue="customer"
					is-readonly
			/>

			<!-- Pay date -->
			<field
					margin="0 0 10px 0"
					width="300px"
					type="text"
					label="Pay date"
					placeholder="MM/DD/YYYY"
					display-format="YYYY-MM-DD hh:mm"
					:dateValue="date"
					@updateDate="onTime"
					is-date
					is-attach
					is-no-title
			/>

			<!-- Amount -->
			<field
					margin="0 0 10px 0"
					width="300px"
					label="Amount"
					placeholder="Enter amount"
					type="number"
					:setInputValue="amount"
					@updateInput="onInput($event, 'amount')"
					icon="dollar"
			/>

			<!-- Invoice -->

			<field
					:class-error="$v.invoiceId && $v.invoiceId.$error"
					margin="0 0 10px 0"
					width="300px"
					label="Invoice"
					placeholder="Select invoice"
					:items="invoiceOptions"
					item-text="label"
					item-value="invoice_id"
					:autocompleteValue="selectedInvoice"
					@updateSelect="onInvoiceSelect"
					is-select
					is-required
					:isClearable="false"
					is-attach
			/>

			<!-- Note -->
			<field
					margin="0 0 20px 0"
					width="300px"
					label="Note"
					placeholder="Enter text"
					:setInputValue="notes"
					@updateInput="onInput($event, 'notes')"
					is-textarea
			/>

			<!-- Payment methods -->
			<template
					v-for="({ id, value, status }, idx) in pay_method"
			>
				<field
						:key="id"
						margin="0 0 10px 0"
						padding="0"
						height="24"
						:idx="idx"
						:label-switch="value"
						:switch-value="status"
						@updateSwitch="onCheckbox"
						is-dense
						is-checkbox
						is-off-style
				/>
			</template>
		</div>
	</drawer>
</template>

<script>
import Drawer from "@/shared/Drawer/Drawer";
import { required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import { mapActions } from "vuex";
import getDefaultDate from "@/helper/getDefaultDate";

export default {
	name: "PaidTransactions",

	mixins: [ validationMixin ],

	components: {
		Drawer
	},

	props: {
		data: {
			type: Object,
			default: () => ({})
		}
	},

	data() {
		return {
			incomeTransaction: null,
			invoice: [],

			invoiceId: null,
			selectedInvoice: null,

			amount: null,
			customer: '',
			notes: '',
			date: getDefaultDate(),
			pay_method: []
		};
	},

	validations: {
		invoiceId: {
			required
		}
	},

	computed: {
		/**
		 * Returns invoice options.
		 *
		 * @returns {Array} Invoice options.
		 */
		invoiceOptions() {
			return (this.invoice || []).map(item => ({
				...item,
				label: `#${ item.invoice_id } — $${ item.need_pay_amount }`
			}));
		}
	},

	methods: {
		...mapActions([
			'onPaidTransactions',
			'setAlert'
		]),

		/**
		 * Updates payment date.
		 *
		 * @param {String} date Payment date.
		 * @returns {void}
		 */
		onTime(date) {
			this.date = date;
		},

		/**
		 * Updates selected invoice.
		 *
		 * @param {Object} event Select event.
		 * @returns {void}
		 */
		onInvoiceSelect({ value }) {
			this.selectedInvoice = value || null;
			this.invoiceId = value ? value.invoice_id : null;

			if (value) {
				this.amount = value.need_pay_amount;
			}
		},

		/**
		 * Closes drawer.
		 *
		 * @param {Boolean} isRefresh Refresh table flag.
		 * @returns {void}
		 */
		onClose(isRefresh) {
			this.$emit('updateClose', isRefresh);
		},

		/**
		 * Saves payment.
		 *
		 * @returns {Promise<void>}
		 */
		async onSave() {
			this.$v.$touch();

			if (this.$v.$error) {
				this.setAlert({
					type: 'error',
					message: 'Please select invoice'
				});

				return;
			}

			const values = {
				invoice_id: this.invoiceId,
				income_transaction_id: this.incomeTransaction.id,
				notes: this.notes || null,
				pay_method: this.pay_method.map(item => ({
					id: item.id,
					status: item.status
				}))
			};

			const status = await this.onPaidTransactions(values);

			if (!status) {
				return;
			}

			this.onClose(true);
		},

		/**
		 * Updates field value.
		 *
		 * @param {Object} event Field event.
		 * @param {String} key Field key.
		 * @returns {void}
		 */
		onInput({ value }, key) {
			this[key] = value;
		},

		/**
		 * Updates payment method.
		 *
		 * @param {Object} event Checkbox event.
		 * @returns {void}
		 */
		onCheckbox({ value, idx }) {
			this.pay_method[idx].status = value;
		},

		/**
		 * Sets payment method by transaction name.
		 *
		 * @returns {void}
		 */
		setDefaultPaymentMethod() {
			if (!this.incomeTransaction || !this.incomeTransaction.name) {
				return;
			}

			const transactionName = String(
				this.incomeTransaction.name
			).toLowerCase();

			this.pay_method = this.pay_method.map(item => {
				const method = String(item.value).toLowerCase();

				let status = false;

				if (
					transactionName.includes('zelle')
						&& method.includes('zelle')
				) {
					status = true;
				}

				if (
					(
						transactionName.includes('ach')
								|| transactionName.includes('асн')
								|| transactionName.includes('wire')
					)
						&& method.includes('ach')
				) {
					status = true;
				}

				return {
					...item,
					status
				};
			});
		},

		/**
		 * Initializes payment data.
		 *
		 * @returns {void}
		 */
		initData() {
			const {
				incomeTransaction = null,
				invoice = [],
				pay_method = []
			} = this.data;

			this.incomeTransaction = incomeTransaction;
			this.invoice = invoice || [];

			this.pay_method = pay_method.map(item => ({
				...item,
				status: false
			}));

			if (!incomeTransaction) {
				return;
			}

			this.amount = incomeTransaction.amount || null;
			this.customer = incomeTransaction.customer || '';
			this.date = getDefaultDate();

			this.setDefaultPaymentMethod();
		},
	},

	mounted() {
		this.initData();
	}
};
</script>

<style scoped lang="scss">
.paidTransactions {
	width: 300px;

	&__invoiceSelect {
		margin-top: 20px;
		margin-bottom: 10px;
	}
}

.subtext {
	color: #71CC82;
	font-size: 16px;
	font-style: normal;
	font-weight: 500;
	line-height: 20px;
	letter-spacing: 0.25px;
}
</style>
